// LocaleContext — the portal's LocaleSync-equivalent.
//
// Nexus's <LocaleSync> applies a per-user ERP locale once `user` loads. The
// portal has no per-user ERP row (cross-repo brief §11.2), so this instead:
//   • exposes the active locale + a `setLocale` the picker calls,
//   • persists the explicit picker choice to localStorage (via i18next's
//     detector cache + resolveLocale's key — one slot, no drift),
//   • keeps <html lang> in sync so the document advertises the right language
//     to the browser / assistive tech,
//   • stays subscribed to i18next 'languageChanged' so any programmatic change
//     is reflected in React state and the <html lang> attribute.
//
// i18next itself is initialized in ./index.ts (imported for side effect before
// render); this provider is a thin React binding over that singleton.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  persistLocale,
  type SupportedLocale,
} from './resolveLocale.js';

type LocaleContextValue = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  supportedLocales: readonly SupportedLocale[];
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function currentLocale(resolved: string | undefined): SupportedLocale {
  return isSupportedLocale(resolved) ? resolved : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    currentLocale(i18n.resolvedLanguage ?? i18n.language),
  );

  // Reflect any language change (picker or programmatic) into React state and
  // the <html lang> attribute.
  useEffect(() => {
    const apply = (lng: string): void => {
      const next = currentLocale(lng);
      setLocaleState(next);
      document.documentElement.setAttribute('lang', next);
    };
    apply(i18n.resolvedLanguage ?? i18n.language);
    i18n.on('languageChanged', apply);
    return () => {
      i18n.off('languageChanged', apply);
    };
  }, [i18n]);

  const setLocale = useCallback(
    (next: SupportedLocale): void => {
      persistLocale(next);
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, supportedLocales: SUPPORTED_LOCALES }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
